import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { getFingerprint } from "@/lib/fingerprint";
import { Hero } from "@/components/tss/Hero";
import { TopBar } from "@/components/tss/TopBar";
import { CategorySection } from "@/components/tss/CategorySection";
import { SubmitBar } from "@/components/tss/SubmitBar";
import { OtpDialog } from "@/components/tss/OtpDialog";
import { Confirmation, ClosedView } from "@/components/tss/Confirmation";
import { AlpanaDivider } from "@/components/tss/Ornaments";
import { ReviewSheet } from "@/components/tss/ReviewSheet";
import { SiteFooter } from "@/components/tss/SiteFooter";
import { MAX_SUBMISSIONS, VoteStatusBanner } from "@/components/tss/VoteChange";

const scrollToCat = (id) => setTimeout(() => document.getElementById(`cat-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);

function useVotingData() {
  const [data, setData] = useState({ config: null, cats: [], coms: [] });
  const load = useCallback(async () => {
    const [c, k, m] = await Promise.all([api.get("/public/config"), api.get("/public/categories"), api.get("/public/committees")]);
    setData({ config: c.data, cats: k.data, coms: m.data });
  }, []);
  useEffect(() => {
    load().catch(() => toast.error("Couldn't load voting data. Check your connection."));
    const t = setInterval(() => load().catch(() => {}), 10000);
    return () => clearInterval(t);
  }, [load]);
  return { ...data, reload: load };
}

export default function VotePage() {
  const { config, cats, coms, reload } = useVotingData();
  const [fp, setFp] = useState(null);
  const [voted, setVoted] = useState({});
  const [picks, setPicks] = useState({});
  const [openId, setOpenId] = useState(null);
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpToken, setOtpToken] = useState(() => sessionStorage.getItem("tss_otp") || "");
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [review, setReview] = useState(false);
  const [details, setDetails] = useState({ name: "", email: "", phone: "", consent: false });
  const [ballot, setBallot] = useState({ count: 0, remaining: 2 });
  const opened = useRef(false);

  useEffect(() => { getFingerprint().then(setFp); }, []);

  const loadMine = useCallback(async (prefill = false) => {
    if (!fp) return null;
    const { data } = await api.post("/votes/mine", { fingerprint: fp, otp_token: otpToken || null });
    const map = Object.fromEntries(data.votes.map((v) => [v.category_id, v.committee_id]));
    setVoted(map);
    if (prefill) setPicks(map);
    setBallot({ count: data.submission_count, remaining: data.changes_remaining });
    return map;
  }, [fp, otpToken]);

  const catsReady = cats.length > 0;
  const catsRef = useRef(cats);
  catsRef.current = cats;

  useEffect(() => {
    if (!catsReady) return;
    loadMine(true).then((map) => {
      if (map && !opened.current) {
        opened.current = true;
        setOpenId(catsRef.current.find((c) => !map[c.id])?.id ?? null);
      }
    }).catch(() => {});
  }, [loadMine, catsReady]);

  const pick = (catId, comId) => {
    const deselect = picks[catId] === comId;
    setPicks((p) => { const n = { ...p }; if (deselect) delete n[catId]; else n[catId] = comId; return n; });
    if (deselect) return;
    const idx = cats.findIndex((c) => c.id === catId);
    const next = [...cats.slice(idx + 1), ...cats.slice(0, idx)].find((c) => !picks[c.id]);
    setTimeout(() => { setOpenId(next?.id ?? null); if (next) scrollToCat(next.id); }, 350);
  };

  const pending = Object.entries(picks).filter(([c]) => cats.some((k) => k.id === c));
  const exhausted = ballot.count >= MAX_SUBMISSIONS;
  const isChange = ballot.count > 0;
  const changed = pending.length !== Object.keys(voted).length || pending.some(([c, m]) => voted[c] !== m);

  const doSubmit = async (token) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/votes", {
        fingerprint: fp, otp_token: token || null,
        voter_name: details.name.trim(), voter_email: details.email.trim(), voter_phone: details.phone.trim(), marketing_consent: details.consent,
        picks: pending.map(([category_id, committee_id]) => ({ category_id, committee_id })),
      });
      setReview(false);
      setReceipt(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
      await loadMine(true);
    } catch (e) {
      const st = e?.response?.status;
      if (st === 401) { sessionStorage.removeItem("tss_otp"); setOtpToken(""); setOtpOpen(true); }
      else if (st === 403) reload();
      else if (st === 409) { setReview(false); loadMine(true); }
      toast.error(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  const submit = () => {
    if (exhausted || !pending.length || !details.name.trim() || !details.email.trim() || !details.phone.trim()) return;
    if (config.otp_required && !otpToken) { setOtpOpen(true); return; }
    doSubmit(otpToken);
  };

  const onVerified = (token) => {
    sessionStorage.setItem("tss_otp", token);
    setOtpToken(token);
    setOtpOpen(false);
    doSubmit(token);
  };

  if (!config) {
    return <div className="grid min-h-screen place-items-center bg-maroon-deep" data-testid="vote-loading"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;
  }
  if (!config.voting_open) {
    return <main className="alpana-bg min-h-screen"><Hero config={config} showStart={false} /><ClosedView leaderboard={config.leaderboard_public} /><SiteFooter /></main>;
  }

  const jump = (id) => { setOpenId(id); scrollToCat(id); };

  const otpDialog = <OtpDialog open={otpOpen} onOpenChange={setOtpOpen} channel={config.otp_channel} onVerified={onVerified} />;

  if (review && pending.length && !exhausted) {
    return (
      <main className="alpana-bg min-h-screen">
        <ReviewSheet picks={pending} cats={cats} coms={coms} details={details} onChange={setDetails}
          onBack={() => setReview(false)} onSubmit={submit} submitting={submitting} ballot={ballot} />
        <SiteFooter />
        {otpDialog}
      </main>
    );
  }

  if (receipt) {
    return (
      <main className="alpana-bg min-h-screen">
        <Confirmation receipt={receipt} cats={cats} coms={coms}
          onChange={() => { setReceipt(null); if (cats[0]) jump(cats[0].id); }} />
        <SiteFooter />
      </main>
    );
  }

  return (
    <main className="alpana-bg min-h-screen pb-28" data-testid="vote-page">
      <Hero config={config} onStart={() => jump(openId || cats[0]?.id)} />
      <TopBar config={config} cats={cats} picks={picks} voted={voted} onJump={jump} />
      <div className="mx-auto max-w-2xl space-y-4 px-4 py-6">
        <VoteStatusBanner ballot={ballot} onChange={() => cats[0] && jump(cats[0].id)} />
        <div className="text-center">
          <h2 className="brush-gold inline-block px-2 font-display text-2xl font-bold text-maroon">{isChange ? "Your vote" : "Cast your votes"}</h2>
          <p className="mt-1 font-bengali text-[15px] text-ink/80">আপনার প্রিয় পুজোকে ভোট দিন</p>
        </div>
        {cats.map((cat, i) => (
          <CategorySection key={cat.id} cat={cat} index={i} committees={coms} open={openId === cat.id}
            onToggle={() => setOpenId(openId === cat.id ? null : cat.id)}
            pick={picks[cat.id]} votedFor={voted[cat.id]} locked={exhausted} onPick={(cid) => pick(cat.id, cid)} />
        ))}
        <AlpanaDivider className="pt-4" />
        <p className="text-center text-xs text-ink-muted">One ballot per device — only your last vote counts. Official judging by Kolkata artists is separate.</p>
      </div>
      <SiteFooter />
      <SubmitBar count={pending.length} total={cats.length} exhausted={exhausted} isChange={isChange} changed={changed} submitting={submitting} onSubmit={() => { setReview(true); window.scrollTo({ top: 0 }); }} onStart={() => jump(openId || cats[0]?.id)} />
      {otpDialog}
    </main>
  );
}
